import { useClerk, useUser } from "@clerk/react-router";
import { useEffect, useState } from "react";
import { FormProvider, useForm, type FieldPath } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  useProfile,
  useProfileFields,
  useUpdateMemberCheckIn,
  useUpdateProfile,
} from "../api";
import { DynamicField } from "../components/forms/DynamicField";
import { ErrorState, SkeletonBlock } from "../components/ui/AsyncState";
import { LockedField } from "../components/ui/LockedField";
import { Modal } from "../components/ui/Modal";
import { Page } from "../components/ui/Page";
import { usePanelPath } from "../routing";
import type { ProfileField, UpdateProfileInput } from "../types";
import {
  buildProfileFieldRows,
  readProfileFieldValues,
} from "../utils/profileFields";
import { isValidUkContactPhone, normalizePhone } from "../utils/validation";

type ProfileEditFormValues = Pick<
  UpdateProfileInput,
  "phone" | "profileFields"
>;

export function ProfileEditPage() {
  const navigate = useNavigate();
  const panelPath = usePanelPath();
  const params = useParams();
  const { closeUserProfile, openUserProfile } = useClerk();
  const { user } = useUser();
  const { data: profile, error, isLoading } = useProfile();
  const {
    data: profileFields,
    error: fieldsError,
    isLoading: fieldsLoading,
  } = useProfileFields();
  const updateProfile = useUpdateProfile();
  const checkInIdParam = params?.checkInId;
  const checkInId = checkInIdParam ? Number(checkInIdParam) : undefined;
  const updateMemberCheckIn = useUpdateMemberCheckIn(
    Number.isFinite(checkInId) ? checkInId : undefined,
  );
  const [showCheckinWelcome, setShowCheckinWelcome] = useState(false);
  const [showCheckinThanks, setShowCheckinThanks] = useState(false);
  useEffect(() => {
    if (checkInIdParam) {
      setShowCheckinWelcome(true);
    }
  }, []);
  const form = useForm<ProfileEditFormValues>({
    defaultValues: {
      phone: "",
      profileFields: {},
    },
  });
  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = form;

  useEffect(() => {
    if (profile && profileFields) {
      reset({
        phone: profile.personal.phone,
        profileFields: readProfileFieldValues(profileFields, profile),
      });
    }
  }, [profile, profileFields, reset]);

  if (isLoading || fieldsLoading) {
    return (
      <Page>
        <div className="profile-wrap">
          <SkeletonBlock count={10} height={42} />
        </div>
      </Page>
    );
  }

  if (error || fieldsError || !profile || !profileFields) {
    return (
      <Page>
        <ErrorState
          error={error ?? fieldsError}
          title="Could not load profile editor"
        />
      </Page>
    );
  }

  const personal = profile.personal;
  const accountEmail =
    user?.primaryEmailAddress?.emailAddress ?? personal.email;

  function openClerkAccountManagement() {
    closeUserProfile();
    window.setTimeout(() => {
      openUserProfile({ __experimental_startPath: "" });
    }, 0);
  }

  function saveChanges(values: ProfileEditFormValues) {
    const input = {
      ...values,
      email: accountEmail,
      phone: values.phone ? normalizePhone(values.phone) : values.phone,
    } satisfies UpdateProfileInput;

    updateProfile.mutate(input, {
      // onSuccess: () => {
      //   navigate(panelPath("/profile"));
      // },
    });
    //optimistic
    navigate(panelPath("/profile"));
  }

  return (
    <Page>
      <Modal
        open={showCheckinWelcome}
        onClose={() => setShowCheckinWelcome(false)}
      >
        <div aria-modal="true" className="modal" role="dialog">
          <div className="modal-header">
            <div className="modal-title title-md">Updating your details</div>
          </div>
          <div className="modal-body">
            <div className="modal-body-text">
              Click the button below to confirm you wish to continue to be part
              of the panel
            </div>
          </div>
          <div className="modal-footer">
            <ErrorState error={updateMemberCheckIn.error} />
            <button
              className="btn btn-full btn-primary"
              disabled={updateMemberCheckIn.isPending}
              onClick={() =>
                updateMemberCheckIn.mutate(
                  {
                    response: "continue",
                    responseDate: new Date(),
                  },
                  {
                    onSuccess: () => {
                      setShowCheckinWelcome(false);
                      setShowCheckinThanks(true);
                    },
                  },
                )
              }
              // style={{ background: "var(--red)", color: "#fff" }}
              type="button"
            >
              {updateMemberCheckIn.isPending ? "Confirming..." : "Confirm"}
            </button>
          </div>
          <div className="modal-body-text" />
          <div className="modal-body">
            <div className="auth-note">
              If you would prefer to no longer be a part of this panel, and
              forfeit any un-redeemed points in your wallet, you can remove your
              account by{" "}
              <Link to={panelPath("/profile?deactivate=true")}>
                clicking here.
              </Link>
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        open={showCheckinThanks}
        onClose={() => setShowCheckinThanks(false)}
      >
        <div aria-modal="true" className="modal" role="dialog">
          <div className="modal-header">
            <div className="modal-title title-md">Thank you!</div>
          </div>
          <div className="modal-body">
            <div className="modal-body-text">
              Now please take a moment to confirm your details are correct on
              this page. Just click the button below, then update your details
              as you wish, clicking the "Save changes" button when you're done.
            </div>
            <div className="modal-body-text">
              Remember - The more complete your profile is, the more tasks
              you'll be accepted for!
            </div>
          </div>

          <div className="modal-footer">
            <button
              className="btn btn-full btn-primary"
              onClick={() => setShowCheckinThanks(false)}
              // style={{ background: "var(--red)", color: "#fff" }}
              type="button"
            >
              Review my details
            </button>
          </div>
        </div>
      </Modal>
      <FormProvider {...form}>
        <form className="profile-wrap" onSubmit={handleSubmit(saveChanges)}>
          <Link className="back-link" to={panelPath("/profile")}>
            ← Back to profile
          </Link>

          <div className="section-hdr">
            <div className="section-hdr-lbl">Personal details</div>
            <span style={{ color: "var(--sand-lt)", fontSize: 11 }}>
              Set at signup - contact support to request a change
            </span>
          </div>

          <div className="field-row">
            <LockedField label="First name" value={personal.firstName} />
            <LockedField label="Last name" value={personal.lastName} />
          </div>
          <div className="field-row">
            <LockedField label="Date of birth" value={personal.dob} />
          </div>
          <div className="field-row">
            <LockedField label="Email address" value={accountEmail} />
            <div className="field field-readonly-hint">
              <label>Manage sign-in account</label>
              <button
                className="btn btn-outline"
                onClick={openClerkAccountManagement}
                type="button"
              >
                Open account settings
              </button>
            </div>
          </div>

          <div className="section-hdr">
            <div className="section-hdr-lbl">Contact details</div>
          </div>

          <div className="field-row">
            <div className="field">
              <label htmlFor="profile-phone">Phone number</label>
              <input
                id="profile-phone"
                placeholder="Enter your phone number"
                {...register("phone", {
                  validate: (value) =>
                    !value || isValidUkContactPhone(value)
                      ? true
                      : "Enter a valid UK mobile or landline number",
                })}
              />
              {errors.phone ? (
                <div className="err-msg">{errors.phone.message}</div>
              ) : null}
            </div>
            <div className="field field-readonly-hint" />
          </div>

          {profileFields.sections.map((section) => (
            <div className="profile-field-section" key={section.title}>
              <div className="section-hdr">
                <div className="section-hdr-lbl">{section.title}</div>
              </div>
              {buildProfileFieldRows(section.fields).map((row) => {
                const rowKey = row.map((field) => field.fieldKey).join(":");
                const isFullRow =
                  row.length === 1 &&
                  (row[0].width === "full" || isMultiValueField(row[0]));

                if (isFullRow) {
                  return <DynamicFormField field={row[0]} key={rowKey} />;
                }

                return (
                  <div className="field-row" key={rowKey}>
                    {row.map((field) => (
                      <DynamicFormField field={field} key={field.fieldKey} />
                    ))}
                  </div>
                );
              })}
            </div>
          ))}

          <div className="profile-actions">
            <button
              className="btn btn-outline"
              disabled={updateProfile.isPending}
              onClick={() => {
                if (profile && profileFields) {
                  reset({
                    phone: profile.personal.phone,
                    profileFields: readProfileFieldValues(
                      profileFields,
                      profile,
                    ),
                  });
                }
                navigate(panelPath("/profile"));
              }}
              type="button"
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              disabled={updateProfile.isPending}
              type="submit"
            >
              {updateProfile.isPending ? "Saving..." : "Save changes"}
            </button>
          </div>
        </form>
      </FormProvider>
    </Page>
  );
}

function DynamicFormField({ field }: { field: ProfileField }) {
  return (
    <DynamicField<ProfileEditFormValues>
      field={field}
      idPrefix="pf"
      name={profileFieldPath(field.fieldKey)}
    />
  );
}

function isMultiValueField(field: ProfileField): boolean {
  return field.type === "multiSelect" || field.type === "checkbox";
}

function profileFieldPath(fieldKey: string): FieldPath<ProfileEditFormValues> {
  return `profileFields.${fieldKey}` as FieldPath<ProfileEditFormValues>;
}
